import { inject, Injectable } from '@angular/core';
import {
  ApplicationInsights,
  ICustomProperties,
  ITelemetryItem,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';
import { AccessRightsService } from './access-rights.service';
import { SwPartNumberService } from './sw-part-number.service';
import { TranslationService } from './translation.service';

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  private appInsights?: ApplicationInsights;
  private enabled = false;
  private accessRightsService = inject(AccessRightsService);
  private swPartNumberService = inject(SwPartNumberService);
  private translationService = inject(TranslationService);
  private currentSwPartNumberId: string | null = null;
  private currentLanguageCode: string | null = null;
  private currentUserName: string | null = null;
  private currentUserRoles: string[] = [];

  constructor() {
    if (environment.aiInstrumentationKey === 'no_ai_key') {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.aiInstrumentationKey,
        enableAutoRouteTracking: false,
        // The unload event is blocked by the browser's permissions policy; pagehide/visibilitychange cover the flush.
        disablePageUnloadEvents: ['unload'],
      },
    });
    this.appInsights.loadAppInsights();

    this.configureUserTelemetryContext();

    this.rewireConsoleError();

    if (this.swPartNumberService.swPartNumberChangeSubject) {
      this.swPartNumberService.swPartNumberChangeSubject.subscribe((swPartNumberId) => {
        this.currentSwPartNumberId = swPartNumberId;
      });
    }

    if (this.translationService.languageChangeSubject) {
      this.translationService.languageChangeSubject.subscribe((languageCode) => {
        this.currentLanguageCode = languageCode;
      });
    }
    this.enabled = true;
  }

  public clearUserContext(): void {
    this.currentUserName = null;
    this.currentUserRoles = [];

    if (!this.enabled) {
      return;
    }

    this.appInsights?.clearAuthenticatedUserContext();
  }

  public logPageView(finalUrl?: string, url?: string): void {
    if (!this.enabled) {
      return;
    }

    const props = {
      swPartNumberId: this.currentSwPartNumberId,
      languageCode: this.currentLanguageCode,
      url: url,
    };
    this.appInsights!.trackPageView({ name: finalUrl, uri: window.location.href, properties: props });
  }

  public logSidebarUsage(blade: string): void {
    if (!this.enabled) {
      return;
    }

    this.logEvent('SidebarUsed', {
      sidebarBlade: blade,
    });
  }

  public logMissingFieldHelp(entityName?: string, fieldName?: string): void {
    if (!this.enabled) {
      return;
    }

    this.logEvent('MissingFieldHelp', {
      entityName: entityName,
      fieldName: fieldName,
    });
  }

  public logEvent(name: string, properties?: ICustomProperties): void {
    if (!this.enabled) {
      return;
    }
    this.appInsights!.trackEvent({ name }, properties);
  }

  public logException(cause: unknown, context: string, properties?: ICustomProperties): void {
    if (!this.enabled) {
      console.debug(cause); // log to console for local development
      return;
    }

    const localProps = {
      context: context,
    };

    this.appInsights!.trackException(
      { exception: cause as Error, severityLevel: SeverityLevel.Error },
      { ...localProps, ...properties }
    );
  }

  private rewireConsoleError(): void {
    console.error = (...args: unknown[]) => {
      const errors = args.filter((a) => a instanceof Error);
      const otherInfo = args.filter((a) => !(a instanceof Error)).join(' | ');

      if (errors.length == 0) {
        this.logException(new Error(otherInfo), 'console.error');
      } else {
        for (const error of errors) {
          this.logException(error, 'console.error', { addContext: otherInfo });
        }
      }
    };
  }

  private configureUserTelemetryContext(): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.addTelemetryInitializer((telemetryItem: ITelemetryItem) => {
      const userName = this.currentUserName;
      const roles = this.currentUserRoles;
      if (!userName && (!roles || roles.length === 0)) {
        return;
      }

      telemetryItem.tags = telemetryItem.tags || {};
      telemetryItem.data = telemetryItem.data || {};

      telemetryItem.data = {
        ...telemetryItem.data,
        userInformation: {
          userName: userName,
          userRoles: roles,
        },
      };
    });

    this.accessRightsService.actOnAccessRights((ar) => {
      this.currentUserName = ar.userName || null;
      this.currentUserRoles = ar.roles ?? [];

      if (this.currentUserName) {
        this.appInsights?.setAuthenticatedUserContext(this.currentUserName);
      }
    });
  }
}
